import { Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { caixaService, commissionService, commissionGoalProgressService, commissionCheckpointService, employeeWalletService, feedback, ICommissionCheckpointView } from '../../../shared/services';
import { IPropsBusinessPage } from '../Business';
import { exportToCsv, formatCurrencyCsv } from '../../../shared/helpers/exportCsv';
import { dateFormat } from '../../../shared/helpers/dateFormat';
import { formatCurrency } from '../../../shared/helpers/formatCurrency';
import { DatePreset, DatePresetSelector, getPresetRange, SummaryCard, CenteredLoading, CommissionSummaryRow, CommissionEmployeeExpandable } from '../../../shared/components';

export const BusinessCommissions: React.FC<IPropsBusinessPage> = ({ business }) => {
  const queryClient = useQueryClient();
  const [preset, setPreset] = useState<DatePreset>('month');
  const [expandedEmployee, setExpandedEmployee] = useState<number | null>(null);
  const [checkpointsByEmployee, setCheckpointsByEmployee] = useState<Record<number, ICommissionCheckpointView[]>>({});
  const [loadingCheckpoints, setLoadingCheckpoints] = useState<Record<number, boolean>>({});
  const [pagarDialogOpen, setPagarDialogOpen] = useState(false);
  const [selectedWalletId, setSelectedWalletId] = useState(0);
  const [pagamentoAmount, setPagamentoAmount] = useState(0);
  const [pagamentoDesc, setPagamentoDesc] = useState('');
  const [isPaying, setIsPaying] = useState(false);
  const [overpaymentConfirm, setOverpaymentConfirm] = useState<{
    walletId: number;
    amount: number;
    desc: string;
    pending: number;
  } | null>(null);

  // ── Pending payout request management ──
  const [processandoSaque, setProcessandoSaque] = useState<{ payoutId: number; approved: boolean } | null>(null);
  const [saqueNotes, setSaqueNotes] = useState('');

  const loadCheckpoints = useCallback(async (employeeId: number) => {
    if (checkpointsByEmployee[employeeId]) return;
    setLoadingCheckpoints(prev => ({ ...prev, [employeeId]: true }));
    try {
      const data = await commissionCheckpointService.getCheckpoints(employeeId, 5);
      setCheckpointsByEmployee(prev => ({ ...prev, [employeeId]: data }));
    } catch {
      // silently fail
    } finally {
      setLoadingCheckpoints(prev => ({ ...prev, [employeeId]: false }));
    }
  }, [checkpointsByEmployee]);

  const { start, end } = useMemo(() => getPresetRange(preset), [preset]);

  // ── Endpoint unificado ──
  const { data: commissions, isFetching } = useQuery({
    queryKey: ['commissions', business.id, start, end],
    queryFn: () => commissionService.getCommissions(business.id, undefined, start, end),
  });

  // Summary por employee, ordenado alfabeticamente
  const employeeSummaries = useMemo(() => (commissions?.map(c => ({
    businessEmployeeId: c.businessEmployeeId,
    employeeName: c.employeeName,
    totalAppointments: c.totalAppointments,
    grossRevenue: c.totalGrossRevenue,
    cardFees: c.totalCardFees,
    netRevenue: c.totalNetRevenue,
    servicesCommissionRate: c.servicesCommissionRate,
    commissionAmount: c.totalCommission,
  })) ?? []).sort((a, b) => a.employeeName.localeCompare(b.employeeName, 'pt-BR')), [commissions]);

  // Indexa detalhamento por employeeId para lookup rápido
  const employeeDetailLookup = useMemo(() => {
    if (!commissions) return {};
    const map: Record<number, typeof commissions[0]> = {};
    for (const emp of commissions) {
      map[emp.businessEmployeeId] = emp;
    }
    return map;
  }, [commissions]);

  const totalCommission = useMemo(() => employeeSummaries?.reduce((t, e) => t + e.commissionAmount, 0) || 0, [employeeSummaries]);
  const totalGrossRevenue = useMemo(() => employeeSummaries?.reduce((t, e) => t + e.grossRevenue, 0) || 0, [employeeSummaries]);

  const { data: wallets } = useQuery({
    queryKey: ['employeeWallets', business.id],
    queryFn: () => employeeWalletService.getByBusinessId(business.id),
  });

  // ── Wallet info: total já pago e pendente por employee ──
  const walletsByEmployeeId = useMemo(() => {
    if (!wallets) return {};
    const map: Record<number, { totalPago: number; pendente: number; payoutHistory: Array<{ id: number; amount: number; status: number; statusName: string; requestedAt: Date; paidAt?: Date; notes?: string }> }> = {};
    for (const w of wallets) {
      const paidRequests = w.payoutRequests.filter(pr => pr.status === 1);
      const totalPago = paidRequests.reduce((s, pr) => s + pr.amount, 0);
      map[w.businessEmployeeId] = {
        totalPago,
        pendente: w.pendingCommissions,
        payoutHistory: paidRequests.map(pr => ({
          id: pr.id,
          amount: pr.amount,
          status: pr.status,
          statusName: pr.statusName,
          requestedAt: pr.requestedAt,
          paidAt: pr.paidAt,
          notes: pr.notes,
        })),
      };
    }
    return map;
  }, [wallets]);

  const totalPagoGeral = useMemo(() =>
    Object.values(walletsByEmployeeId).reduce((s, w) => s + w.totalPago, 0),
  [walletsByEmployeeId]);

  const totalPendenteGeral = useMemo(() =>
    Object.values(walletsByEmployeeId).reduce((s, w) => s + w.pendente, 0),
  [walletsByEmployeeId]);

  // ── Pending payout requests across all employees ──
  const pendingPayouts = useMemo(() => {
    if (!wallets) return [];
    const result: Array<{ payoutId: number; walletId: number; employeeName: string; amount: number; requestedAt: Date }> = [];
    for (const wallet of wallets) {
      for (const pr of wallet.payoutRequests) {
        if (pr.status === 0) {
          result.push({
            payoutId: pr.id,
            walletId: wallet.id,
            employeeName: wallet.employeeName,
            amount: pr.amount,
            requestedAt: pr.requestedAt,
          });
        }
      }
    }
    return result.sort((a, b) => new Date(a.requestedAt).getTime() - new Date(b.requestedAt).getTime());
  }, [wallets]);

  useQuery({
    queryKey: ['currentCaixa', business.id],
    queryFn: () => caixaService.getCurrent(business.id),
  });

  const { data: goalProgress } = useQuery({
    queryKey: ['goalProgress', expandedEmployee],
    queryFn: () => commissionGoalProgressService.getGoalProgress(expandedEmployee!),
    enabled: !!expandedEmployee,
  });

  const handleProcessarSaque = async () => {
    if (!processandoSaque) return;
    try {
      await employeeWalletService.processPayout({
        payoutRequestId: processandoSaque.payoutId,
        approved: processandoSaque.approved,
        notes: saqueNotes || undefined,
      });
      feedback(processandoSaque.approved ? 'Saque aprovado com sucesso!' : 'Saque recusado.', 'success');
      setProcessandoSaque(null);
      setSaqueNotes('');
      queryClient.invalidateQueries({ queryKey: ['employeeWallets', business.id] });
      queryClient.invalidateQueries({ queryKey: ['commissions', business.id] });
      queryClient.invalidateQueries({ queryKey: ['currentCaixa', business.id] });
      queryClient.invalidateQueries({ queryKey: ['transactions', business.id] });
    } catch (err) {
      feedback(err instanceof Error ? err.message : String(err), 'error');
    }
  };

  const handlePagarProfissional = async (confirmedOverpayment?: { walletId: number; amount: number; desc: string }) => {
    const walletId = confirmedOverpayment?.walletId ?? selectedWalletId;
    const amount = confirmedOverpayment?.amount ?? pagamentoAmount;
    const desc = confirmedOverpayment?.desc ?? pagamentoDesc;

    if (!walletId || amount <= 0) return;
    setIsPaying(true);

    try {
      await employeeWalletService.directPayout({
        employeeWalletId: walletId,
        amount,
        description: desc || undefined,
      });

      feedback('Pagamento realizado com sucesso!', 'success');
      setPagarDialogOpen(false);
      setSelectedWalletId(0);
      setPagamentoAmount(0);
      setPagamentoDesc('');
      queryClient.invalidateQueries({ queryKey: ['employeeWallets', business.id] });
      queryClient.invalidateQueries({ queryKey: ['commissions', business.id] });
      queryClient.invalidateQueries({ queryKey: ['currentCaixa', business.id] });
      queryClient.invalidateQueries({ queryKey: ['transactions', business.id] });
    } catch (err) {
      feedback(err instanceof Error ? err.message : String(err), 'error');
    } finally {
      setIsPaying(false);
    }
  };

  return (
    <Box display='flex' flexDirection='column' gap={2}>
      {/* Date preset */}
      <DatePresetSelector value={preset} onChange={setPreset} />

      {/* Action buttons */}
      <Box display='flex' justifyContent='flex-end' gap={1}>
        <Button
          size='small'
          variant='contained'
          color='success'
          onClick={() => setPagarDialogOpen(true)}
        >
          Pagar Profissional
        </Button>
        <Button
          size='small'
          variant='text'
          disabled={!employeeSummaries?.length}
          onClick={() => {
            if (employeeSummaries) {
              exportToCsv(employeeSummaries, [
                { header: 'Profissional', key: 'employeeName' },
                { header: 'Atendimentos', key: 'totalAppointments' },
                { header: 'Receita Bruta', key: 'grossRevenue', format: formatCurrencyCsv },
                { header: 'Taxa Cartão', key: 'cardFees', format: formatCurrencyCsv },
                { header: 'Receita Líquida', key: 'netRevenue', format: formatCurrencyCsv },
                { header: 'Comissão', key: 'commissionAmount', format: formatCurrencyCsv },
              ], `comissoes-${preset}`);
            }
          }}
        >
          Exportar CSV
        </Button>
      </Box>

      {/* Summary Cards */}
      <SummaryCard
        cards={[
          { title: 'Comissão (período)', value: formatCurrency(totalCommission), color: 'primary.main' },
          { title: 'Receita Bruta', value: formatCurrency(totalGrossRevenue), color: 'success.main' },
          { title: 'Total Já Pago', value: formatCurrency(totalPagoGeral), color: 'info.main' },
          { title: 'Total Pendente', value: formatCurrency(totalPendenteGeral), color: totalPendenteGeral < 0 ? 'error.main' : 'warning.main' },
        ]}
      />

      {/* ── Pending payout requests ── */}
      {pendingPayouts.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant='h6' gutterBottom>Solicitações de saque pendentes</Typography>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Profissional</TableCell>
                  <TableCell align='right'>Valor</TableCell>
                  <TableCell>Solicitado em</TableCell>
                  <TableCell align='right'>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingPayouts.map((p) => (
                  <TableRow key={p.payoutId}>
                    <TableCell>{p.employeeName}</TableCell>
                    <TableCell align='right'>{formatCurrency(p.amount)}</TableCell>
                    <TableCell>{dateFormat(p.requestedAt, 'DD/MM/YYYY HH:mm')}</TableCell>
                    <TableCell align='right'>
                      <Box display='flex' gap={0.5} justifyContent='flex-end'>
                        <Button
                          size='small'
                          variant='contained'
                          color='success'
                          onClick={() => {
                            setProcessandoSaque({ payoutId: p.payoutId, approved: true });
                            setSaqueNotes('');
                          }}
                        >
                          Aprovar
                        </Button>
                        <Button
                          size='small'
                          variant='outlined'
                          color='error'
                          onClick={() => {
                            setProcessandoSaque({ payoutId: p.payoutId, approved: false });
                            setSaqueNotes('');
                          }}
                        >
                          Recusar
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {isFetching ? (
        <CenteredLoading />
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox' />
                <TableCell>Profissional</TableCell>
                <TableCell align='right'>Atend.</TableCell>
                <TableCell align='right'>Receita Bruta</TableCell>
                <TableCell align='right'>Taxa Cartão</TableCell>
                <TableCell align='right'>Receita Líquida</TableCell>
                <TableCell align='right'>%</TableCell>
                <TableCell align='right'>Comissão</TableCell>
                <TableCell align='right'>Já Pago</TableCell>
                <TableCell align='right'>Pendente</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employeeSummaries?.map((item) => {
                const isExpanded = expandedEmployee === item.businessEmployeeId;
                const empDetail = employeeDetailLookup[item.businessEmployeeId];
                const checkpoints = checkpointsByEmployee[item.businessEmployeeId];
                const walletInfo = walletsByEmployeeId[item.businessEmployeeId];
                const totalPago = walletInfo?.totalPago ?? 0;
                const pendente = walletInfo?.pendente ?? 0;

                return (
                  <Fragment key={item.businessEmployeeId}>
                    <CommissionSummaryRow
                      employeeName={item.employeeName}
                      totalAppointments={item.totalAppointments}
                      grossRevenue={item.grossRevenue}
                      cardFees={item.cardFees}
                      netRevenue={item.netRevenue}
                      servicesCommissionRate={item.servicesCommissionRate}
                      commissionAmount={item.commissionAmount}
                      totalPago={totalPago}
                      pendente={pendente}
                      isExpanded={isExpanded}
                      onToggle={() => {
                        const next = isExpanded ? null : item.businessEmployeeId;
                        setExpandedEmployee(next);
                        if (next !== null) {
                          loadCheckpoints(next);
                        }
                      }}
                    />
                    <TableRow key={`${item.businessEmployeeId}-expand`}>
                      <TableCell sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }} colSpan={10}>
                        <CommissionEmployeeExpandable
                          expanded={isExpanded}
                          payoutHistory={walletInfo?.payoutHistory}
                          items={empDetail?.items ?? []}
                          totalGrossRevenue={empDetail?.totalGrossRevenue ?? 0}
                          totalCommission={empDetail?.totalCommission ?? 0}
                          checkpoints={checkpoints}
                          loadingCheckpoints={!!loadingCheckpoints[item.businessEmployeeId]}
                          goalProgress={goalProgress}
                        />
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Pagar Profissional Dialog */}
      <Dialog open={pagarDialogOpen} onClose={() => setPagarDialogOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle>Pagar Profissional</DialogTitle>
        <DialogContent>
          <Box display='flex' flexDirection='column' gap={2} mt={1}>
            <Typography variant='body2' fontWeight={500}>Profissional</Typography>
            <Select
              value={selectedWalletId}
              onChange={(e) => {
                const walletId = Number(e.target.value);
                const wallet = wallets?.find(w => w.id === walletId);
                setSelectedWalletId(walletId);
                setPagamentoAmount(wallet?.pendingCommissions ?? 0);
                setPagamentoDesc(wallet ? `Pagamento - ${wallet.employeeName}` : '');
              }}
              fullWidth
              size='small'
              displayEmpty
            >
              <MenuItem value={0} disabled>Selecione...</MenuItem>
              {wallets?.map(w => (
                <MenuItem key={w.id} value={w.id}>
                  {w.employeeName} — A receber: {formatCurrency(w.pendingCommissions)}
                </MenuItem>
              ))}
            </Select>

            <Typography variant='body2' fontWeight={500}>Valor (R$)</Typography>
            <TextField
              size='small'
              fullWidth
              placeholder='0,00'
              value={pagamentoAmount > 0 ? pagamentoAmount.toFixed(2) : ''}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9]/g, '');
                setPagamentoAmount(raw ? parseFloat(raw) / 100 : 0);
              }}
              InputProps={{
                startAdornment: <Typography variant='body2' sx={{ mr: 0.5, color: 'text.secondary' }}>R$</Typography>,
              }}
              inputProps={{
                inputMode: 'decimal',
              }}
            />

            <Typography variant='body2' fontWeight={500}>Descrição</Typography>
            <TextField
              size='small'
              fullWidth
              value={pagamentoDesc}
              onChange={(e) => setPagamentoDesc(e.target.value)}
              placeholder='Ex: Pagamento semanal'
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPagarDialogOpen(false)}>Cancelar</Button>
          <Button
            variant='contained'
            onClick={() => {
              const wallet = wallets?.find(w => w.id === selectedWalletId);
              if (wallet && pagamentoAmount > wallet.pendingCommissions) {
                setOverpaymentConfirm({
                  walletId: selectedWalletId,
                  amount: pagamentoAmount,
                  desc: pagamentoDesc,
                  pending: wallet.pendingCommissions,
                });
              } else {
                handlePagarProfissional();
              }
            }}
            disabled={!selectedWalletId || pagamentoAmount <= 0 || isPaying}
          >
            {isPaying ? 'Processando...' : 'Confirmar Pagamento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Aprovar/Recusar Saque Dialog */}
      <Dialog open={!!processandoSaque} onClose={() => setProcessandoSaque(null)} fullWidth maxWidth='sm'>
        <DialogTitle>{processandoSaque?.approved ? 'Aprovar saque' : 'Recusar saque'}</DialogTitle>
        <DialogContent>
          <Box display='flex' flexDirection='column' gap={2} mt={1}>
            <Typography variant='body2' color='text.secondary'>
              {processandoSaque?.approved
                ? 'Confirma a aprovação deste saque? O valor será deduzido das comissões pendentes do profissional.'
                : 'Confirma a recusa deste saque? O valor permanecerá como comissão pendente.'}
            </Typography>
            <TextField
              label='Observações (opcional)'
              size='small'
              fullWidth
              multiline
              rows={2}
              value={saqueNotes}
              onChange={(e) => setSaqueNotes(e.target.value)}
              placeholder={processandoSaque?.approved ? 'Ex: Saque aprovado' : 'Ex: Saldo insuficiente'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessandoSaque(null)}>Cancelar</Button>
          <Button
            variant='contained'
            color={processandoSaque?.approved ? 'success' : 'error'}
            onClick={handleProcessarSaque}
          >
            {processandoSaque?.approved ? 'Confirmar Aprovação' : 'Confirmar Recusa'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Overpayment confirmation dialog ── */}
      <Dialog open={!!overpaymentConfirm} onClose={() => setOverpaymentConfirm(null)} fullWidth maxWidth='sm'>
        <DialogTitle sx={{ color: 'warning.main', fontWeight: 700 }}>
          Atenção: Pagamento acima do pendente
        </DialogTitle>
        <DialogContent>
          {overpaymentConfirm && (
            <Box display='flex' flexDirection='column' gap={2} mt={1}>
              <Typography variant='body2' color='text.secondary'>
                O profissional tem <strong>{formatCurrency(overpaymentConfirm.pending)}</strong> pendente,
                mas você está pagando <strong>{formatCurrency(overpaymentConfirm.amount)}</strong>.
              </Typography>
              <Typography variant='body2' color='error.main' fontWeight={600}>
                O saldo de comissão do profissional ficará negativo em{' '}
                <strong>{formatCurrency(overpaymentConfirm.pending - overpaymentConfirm.amount)}</strong>,
                ou seja, o profissional passará a dever esse valor.
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Conforme novas comissões forem geradas, o saldo será ajustado até regularizar.
              </Typography>
              <Typography variant='body2' sx={{ mt: 1 }}>
                Deseja continuar com o pagamento?
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverpaymentConfirm(null)} color='inherit'>
            Cancelar
          </Button>
          <Button
            variant='contained'
            color='warning'
            onClick={() => {
              const c = overpaymentConfirm;
              setOverpaymentConfirm(null);
              if (c) handlePagarProfissional(c);
            }}
          >
            Sim, pagar mesmo assim
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
